import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  Inbox,
  Mail,
  CheckCircle2,
  Trash2,
  ExternalLink,
  Circle,
} from "lucide-react";
import { t } from "@/i18n";

const STATUS_LABEL = { new: "Nouveau", read: "Lu", resolved: "Résolu" };
const STATUS_COLOR = {
  new: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  read: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  resolved: "bg-green-500/15 text-green-700 dark:text-green-300",
};

export default function AdminSupportMessages() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [filter, setFilter] = useState("all");
  const [messages, setMessages] = useState(null);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (user && user.role !== "admin") nav("/dashboard");
  }, [user, nav]);

  const load = useCallback(() => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api
      .get(`/admin/support/messages${q}`)
      .then((r) => {
        setMessages(r.data.messages || []);
        setUnread(r.data.unread || 0);
      })
      .catch(() => setMessages([]));
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    setMessages((ms) => ms.map((m) => (m.id === id ? { ...m, status } : m)));
    try {
      await api.put(`/admin/support/messages/${id}`, { status });
    } catch {
      load();
    }
  };

  const remove = async (id) => {
    setMessages((ms) => ms.filter((m) => m.id !== id));
    try {
      await api.delete(`/admin/support/messages/${id}`);
    } catch {
      load();
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Inbox size={26} className="text-primary" />
        <div>
          <h1 className="font-head text-2xl font-bold">{t("adminsupportmessages.messages_recus")}</h1>
          <p className="text-sm text-muted-foreground">
            {unread} nouveau{unread > 1 ? "x" : ""} message{unread > 1 ? "s" : ""}
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        {[
          { id: "all", label: "Tous" },
          { id: "new", label: "Nouveaux" },
          { id: "read", label: "Lus" },
          { id: "resolved", label: "Résolus" },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3.5 py-2 rounded-xl text-sm font-medium border transition ${
              filter === f.id
                ? "bg-primary text-primary-foreground border-primary"
                : "hover:bg-secondary"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {messages === null && (
        <p className="text-muted-foreground text-sm">{t("lessoneditor.chargement")}</p>
      )}

      {messages !== null && messages.length === 0 && (
        <div className="rounded-2xl border bg-card p-8 text-center text-muted-foreground text-sm">
          {t("adminsupportmessages.aucun_message_ici_pour_l_instant")}
        </div>
      )}

      <div className="space-y-3">
        {(messages || []).map((m) => (
          <div
            key={m.id}
            className={`rounded-2xl border p-4 ${m.status === "new" ? "border-amber-500/30" : ""}`}
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {m.status === "new" && (
                    <Circle size={8} className="fill-amber-500 text-amber-500 shrink-0" />
                  )}
                  <span className="text-sm font-semibold truncate">
                    {m.user_name || m.user_email || "Anonyme"}
                  </span>
                  <span
                    className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[m.status]}`}
                  >
                    {STATUS_LABEL[m.status] || m.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  {m.user_email && (
                    <a
                      href={`mailto:${m.user_email}`}
                      className="flex items-center gap-1 hover:underline"
                    >
                      <Mail size={12} /> {m.user_email}
                    </a>
                  )}
                  {m.page && (
                    <span className="flex items-center gap-1">
                      <ExternalLink size={12} /> {m.page}
                    </span>
                  )}
                  <span>{(m.created_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
              </div>
            </div>

            <p className="text-sm whitespace-pre-wrap mb-3">{m.message}</p>

            <div className="flex gap-2">
              {m.status !== "read" && (
                <button
                  onClick={() => setStatus(m.id, "read")}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border hover:bg-secondary transition"
                >
                  {t("adminsupportmessages.marquer_lu")}
                </button>
              )}
              {m.status !== "resolved" && (
                <button
                  onClick={() => setStatus(m.id, "resolved")}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border hover:bg-secondary transition"
                >
                  <CheckCircle2 size={13} /> {t("adminsupportmessages.resolu")}
                </button>
              )}
              <button
                onClick={() => remove(m.id)}
                className="ml-auto flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-destructive hover:bg-destructive/10 transition"
              >
                <Trash2 size={13} /> {t("adminsupportmessages.supprimer")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
